/**
 * @file sync.normalizers.ts
 *
 * Funções **puras** de normalização de dados para o pipeline de sincronização offline.
 *
 * Por que existem essas funções?
 * A Generic API às vezes devolve campos como string "null", "undefined", ou valores
 * booleanos como "true"/"false"/"1"/"0". Essas funções garantem que os dados salvos
 * no IndexedDB sejam sempre tipados corretamente, independentemente do formato bruto
 * recebido da API.
 *
 * Todas as funções aqui são puras (sem efeitos colaterais, sem injeção de dependência)
 * e podem ser testadas de forma totalmente isolada.
 */

import { B2BUnitDetails } from '@shared/models/b2b-unit-details.model';
import {
  LocalDeliveryRoute,
  LocalFinancialTitle,
  LocalMinimumOrderRule,
  LocalOrderDeadline,
  LocalOrderReference,
} from '@asm/features/quick-order/models/offline-reference-data.model';
import { CurrentB2BUnitApiItem, ProductMedia } from '@asm/features/sync/services/sync/sync.interfaces';
import { generateUUID } from '@shared/helpers/generate-uuid';

// ─────────────────────────────────────────────────────────────────────────────
// Primitivos
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sanitiza um valor arbitrário para `string | undefined`.
 *
 * - Converte para string e faz trim.
 * - Retorna `undefined` se o resultado for vazio, `"null"` ou `"undefined"`.
 */
export function normalizeTemplateValue(value: unknown): string | undefined {
  const normalized = String(value ?? '').trim();

  if (!normalized || normalized === 'null' || normalized === 'undefined') {
    return undefined;
  }

  return normalized;
}

/**
 * Converte um valor de tipo desconhecido para `boolean`.
 *
 * Aceita:
 * - `true` / `false` nativos
 * - Strings `"true"`, `"1"` → `true`
 * - Qualquer outra coisa → `false`
 */
export function normalizeBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();
  return ['true', '1', 'x', 'yes', 'sim'].includes(normalized);
}

// ─────────────────────────────────────────────────────────────────────────────
// Mídia
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sanitiza um objeto `ProductMedia`, descartando campos que sejam strings
 * de placeholder como `"null"` ou `"undefined"`.
 *
 * Retorna `null` se não houver nenhum campo com valor real.
 */
export function normalizeMedia(media?: ProductMedia | null): ProductMedia | null {
  if (!media) {
    return null;
  }

  const normalized: ProductMedia = {
    code: normalizeTemplateValue(media.code),
    hashName: normalizeTemplateValue(media.hashName),
    originalName: normalizeTemplateValue(media.originalName),
    mimeType: normalizeTemplateValue(media.mimeType),
    path: normalizeTemplateValue(media.path),
    url: normalizeTemplateValue(media.url),
    offlineUrl: normalizeTemplateValue(media.offlineUrl) ?? null,
  };

  // Descarta o objeto inteiro se não houver nenhum campo com valor real
  return Object.values(normalized).some(Boolean) ? normalized : null;
}

/**
 * Resolve a URL que deve ser usada como fonte offline de uma mídia já processada.
 *
 * Prioridade: `offlineUrl` (data-URL em base64) → `url` (URL remota original).
 * Retorna `null` se não houver nenhuma URL válida.
 */
export function resolveOfflineMediaSource(media?: ProductMedia | null): string | null {
  const offlineUrl = String(media?.offlineUrl ?? '').trim();
  if (offlineUrl) return offlineUrl;

  const url = String(media?.url ?? '').trim();
  return url || null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Entidades de domínio
// ─────────────────────────────────────────────────────────────────────────────

/** Normaliza um item bruto da API para `LocalDeliveryRoute`. */
export function normalizeDeliveryRoute(item: Partial<LocalDeliveryRoute>): LocalDeliveryRoute {
  return {
    id: normalizeTemplateValue(item.id) ?? generateUUID(),
    b2bUnitCode: normalizeTemplateValue(item.b2bUnitCode) ?? '',
    warehouseCode: normalizeTemplateValue(item.warehouseCode) ?? '',
    sunday: normalizeBoolean(item.sunday),
    monday: normalizeBoolean(item.monday),
    tuesday: normalizeBoolean(item.tuesday),
    wednesday: normalizeBoolean(item.wednesday),
    thursday: normalizeBoolean(item.thursday),
    friday: normalizeBoolean(item.friday),
    saturday: normalizeBoolean(item.saturday),
  };
}

/** Normaliza um item bruto da API para `LocalOrderDeadline`. */
export function normalizeOrderDeadline(item: Partial<LocalOrderDeadline>): LocalOrderDeadline {
  return {
    id: normalizeTemplateValue(item.id) ?? generateUUID(),
    deadline: normalizeTemplateValue(item.deadline) ?? '',
    warehouseId: normalizeTemplateValue(item.warehouseId) ?? null,
    warehouseCode: normalizeTemplateValue(item.warehouseCode) ?? null,
    employeeId: normalizeTemplateValue(item.employeeId) ?? null,
    employeeUid: normalizeTemplateValue(item.employeeUid) ?? null,
  };
}

/** Normaliza um item bruto da API para `LocalMinimumOrderRule`. */
export function normalizeMinimumOrderRule(item: Partial<LocalMinimumOrderRule>): LocalMinimumOrderRule {
  return {
    id: normalizeTemplateValue(item.id) ?? generateUUID(),
    minimumOrderValue: normalizeTemplateValue(item.minimumOrderValue) ?? null,
    minimumWeight: normalizeTemplateValue(item.minimumWeight) ?? null,
    shouldBlockCheckout: normalizeBoolean(item.shouldBlockCheckout),
    distributionChannelCode: normalizeTemplateValue(item.distributionChannelCode) ?? null,
    segmentationCode: normalizeTemplateValue(item.segmentationCode) ?? null,
    warehouseCode: normalizeTemplateValue(item.warehouseCode) ?? null,
  };
}

/** Normaliza um item bruto da API para `LocalFinancialTitle`. */
export function normalizeFinancialTitle(item: Partial<LocalFinancialTitle>): LocalFinancialTitle {
  return {
    id: normalizeTemplateValue(item.id) ?? generateUUID(),
    code: normalizeTemplateValue(item.code) ?? null,
    description: normalizeTemplateValue(item.description) ?? null,
    paymentDate: normalizeTemplateValue(item.paymentDate) ?? null,
    dueDate: normalizeTemplateValue(item.dueDate) ?? null,
    value: normalizeTemplateValue(item.value) ?? null,
    b2bUnitUid: normalizeTemplateValue(item.b2bUnitUid) ?? null,
  };
}

/** Normaliza um item bruto da API para `LocalOrderReference`. */
export function normalizeOrderReference(item: Partial<LocalOrderReference>): LocalOrderReference {
  return {
    code: normalizeTemplateValue(item.code) ?? generateUUID(),
    status: normalizeTemplateValue(item.status) ?? null,
    desiredDeliveryDate: normalizeTemplateValue(item.desiredDeliveryDate) ?? null,
    totalPrice: normalizeTemplateValue(item.totalPrice),
    totalWeight: normalizeTemplateValue(item.totalWeight),
    createdAt: normalizeTemplateValue(item.createdAt) ?? null,
    unitUid: normalizeTemplateValue(item.unitUid) ?? null,
    customerUid: normalizeTemplateValue(item.customerUid) ?? null,
  };
}

/**
 * Normaliza o item bruto de `CurrentB2BUnit` para `B2BUnitDetails`.
 *
 * Retorna `null` se os campos obrigatórios `uid` e `name` não estiverem presentes.
 *
 * Lógica de endereço:
 * 1. Tenta encontrar o primeiro endereço marcado como `shippingAddress = true`.
 * 2. Se não encontrar, usa o primeiro endereço da lista (fallback).
 */
export function normalizeCurrentB2BUnit(item?: CurrentB2BUnitApiItem): B2BUnitDetails | null {
  const uid = normalizeTemplateValue(item?.uid);
  const name = normalizeTemplateValue(item?.name);

  if (!uid || !name) {
    return null;
  }

  const normalizedAddresses = item?.addresses?.map(address => ({
    streetname: normalizeTemplateValue(address.streetname),
    streetnumber: normalizeTemplateValue(address.streetnumber),
    complement: normalizeTemplateValue(address.complement),
    district: normalizeTemplateValue(address.district),
    town: normalizeTemplateValue(address.town),
    region: normalizeTemplateValue(address.region),
    country: normalizeTemplateValue(address.country),
    postalcode: normalizeTemplateValue(address.postalcode),
    shippingAddress: normalizeBoolean(address.shippingAddress),
  }));

  // Prefere o endereço de entrega; cai no primeiro disponível como fallback
  const deliveryAddress = normalizedAddresses?.find(address => address.shippingAddress) ?? normalizedAddresses?.[0];

  const mainContact = item?.defaultCustomers?.[0]
    ? {
        name: normalizeTemplateValue(item.defaultCustomers[0].name) ?? '',
        phone1: normalizeTemplateValue(item.defaultCustomers[0].phone1) ?? '',
        phone2: normalizeTemplateValue(item.defaultCustomers[0].phone2),
        whatsapp: normalizeTemplateValue(item.defaultCustomers[0].whatsapp),
        email: normalizeTemplateValue(item.defaultCustomers[0].email) ?? '',
      }
    : undefined;

  const normalizedSuperUnits =
    item?.superUnits?.map(superUnit => ({
      uid: normalizeTemplateValue(superUnit.uid),
      icmstaxpayer: normalizeBoolean(superUnit.icmstaxpayer),
    })) ?? [];

  return {
    uid,
    name,
    description: normalizeTemplateValue(item?.description),
    segmentation: normalizeTemplateValue(item?.segmentation),
    salesOrganization: normalizeTemplateValue(item?.salesOrganization),
    salesRegion: normalizeTemplateValue(item?.salesRegion),
    cluster: normalizeTemplateValue(item?.cluster),
    supplierCenter: normalizeTemplateValue(item?.supplierCenter),
    distributionChannel: normalizeTemplateValue(item?.distributionChannel),
    stGroup: normalizeTemplateValue(item?.stGroup),
    industrialSector: normalizeTemplateValue(item?.industrialSector),
    icmstaxpayer: normalizeBoolean(item?.icmstaxpayer),
    paymentMode: normalizeTemplateValue(item?.paymentMode),
    paymentModeCondition: normalizeTemplateValue(item?.paymentModeCondition),
    cnpj: normalizeTemplateValue(item?.cnpj),
    creditLimit: Number(normalizeTemplateValue(item?.creditLimit) ?? 0),
    deliveryAddress,
    mainContact,
    superUnits: normalizedSuperUnits,
    taxDomicile: normalizeTemplateValue(item?.taxDomicile),
    priceList: normalizeTemplateValue(item?.priceList),
  };
}

// ── OrderHistory normalizer ───────────────────────────────────────────────────

import {
  LocalOrderHistoryConsignment,
  LocalOrderHistoryEntry,
  LocalOrderHistoryItem,
} from '@asm/features/sync/models/order.model';
import { OrderHistoryApiItem } from '@asm/features/sync/services/sync/sync.interfaces';
import { StockAllocation } from '@asm/features/quick-order/models/stock-allocation.model';

/**
 * Parses a stock allocation array that may arrive as a JSON string or as an
 * already-decoded array from the Generic API template engine.
 * Returns null if absent, empty, or unparseable.
 */
function parseJsonAllocationArray(value: unknown): StockAllocation[] | null {
  if (Array.isArray(value)) {
    return value.length > 0 ? (value as StockAllocation[]) : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return null;
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) && parsed.length > 0 ? (parsed as StockAllocation[]) : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Achata a lista de orders com arrays de entries e consignments em registros
 * individuais: uma linha por (order, consignment). Se um order não tiver
 * consignments, gera uma linha com campos ERP nulos.
 *
 * Todos os dados completos (entries + consignments) ficam embutidos em cada
 * linha para permitir uma futura tela de detalhe sem chamadas adicionais.
 */
export function flattenOrderHistory(items: OrderHistoryApiItem[]): LocalOrderHistoryItem[] {
  console.log(items);
  const teste = items.flatMap(order => {
    const orderCode = String(order.code ?? '');
    const entries: LocalOrderHistoryEntry[] = (Array.isArray(order.entries) ? order.entries : []).map(e => ({
      entryNumber: Number(e.entryNumber ?? 0),
      qty: Number(e.qty ?? 0),
      basePrice: Number(normalizeTemplateValue(String(e.basePrice ?? '0')) ?? 0),
      totalPrice: Number(normalizeTemplateValue(String(e.totalPrice ?? '0')) ?? 0),
      totalDiscount: Number(normalizeTemplateValue(String(e.totalDiscount ?? '0')) ?? 0),
      totalTax: Number(normalizeTemplateValue(String(e.totalTax ?? '0')) ?? 0),
      totalWeight: Number(normalizeTemplateValue(String(e.totalWeight ?? '0')) ?? 0),
      ipi: Number(normalizeTemplateValue(String(e.ipi ?? '0')) ?? 0),
      st: Number(normalizeTemplateValue(String(e.st ?? '0')) ?? 0),
      icms: Number(normalizeTemplateValue(String(e.icms ?? '0')) ?? 0),
      warehouseCode: normalizeTemplateValue(e.warehouseCode) ?? null,
      observation: normalizeTemplateValue(e.observation) ?? null,
      rejected: normalizeBoolean(e.rejected),
      message: normalizeTemplateValue(e.message) ?? null,
      status: (normalizeTemplateValue(e.status) as LocalOrderHistoryEntry['status']) ?? null,
      productCode: String(e.product?.code ?? ''),
      productName: String(e.product?.name ?? ''),
      productEan: normalizeTemplateValue(e.product?.sapEAN ?? undefined) ?? null,
      conditions: Array.isArray(e.conditions) ? e.conditions.filter((c): c is string => typeof c === 'string') : [],
      originalStockAllocations: parseJsonAllocationArray(e.originalStockAllocations),
      adjustedStockAllocations: parseJsonAllocationArray(e.adjustedStockAllocations),
    }));

    const consignments: LocalOrderHistoryConsignment[] = (
      Array.isArray(order.consingments) ? order.consingments : []
    ).map(c => ({
      code: String(c.code ?? ''),
      erpOrderCode: normalizeTemplateValue(c.erpOrderCode) ?? null,
      sapStatus: normalizeTemplateValue(c.sapStatus) ?? null,
      warehouse: normalizeTemplateValue(c.warehouse) ?? null,
      totalPrice: Number(normalizeTemplateValue(String(c.totalPrice ?? '0')) ?? 0),
    }));

    const base = {
      orderCode,
      createdAt: String(order.createdAt ?? ''),
      totalPrice: Number(normalizeTemplateValue(String(order.totalPrice ?? '0')) ?? 0),
      totalTax: Number(normalizeTemplateValue(String(order.totalTax ?? '0')) ?? 0),
      totalDiscounts: Number(normalizeTemplateValue(String(order.totalDiscounts ?? '0')) ?? 0),
      ipi: Number(normalizeTemplateValue(String(order.ipi ?? '0')) ?? 0),
      st: Number(normalizeTemplateValue(String(order.st ?? '0')) ?? 0),
      icms: Number(normalizeTemplateValue(String(order.icms ?? '0')) ?? 0),
      platformStatus: normalizeTemplateValue(order.platformStatus) ?? null,
      isQuotation: normalizeBoolean(order.isQuotation),
      freightTypeCode: normalizeTemplateValue(order.freightTypeCode) ?? null,
      freightTypeDescription: normalizeTemplateValue(order.freightTypeDescription) ?? null,
      documentTypeCode: normalizeTemplateValue(order.documentTypeCode) ?? null,
      documentTypeDescription: normalizeTemplateValue(order.documentTypeDescription) ?? null,
      purchaseOrder: normalizeTemplateValue(order.purchaseOrder) ?? null,
      observation: normalizeTemplateValue(order.observation) ?? null,
      paymentLink: normalizeTemplateValue(order.paymentLink) ?? null,
      paymentLinkExpiresAt: normalizeTemplateValue(order.paymentLinkExpiresAt) ?? null,
      desiredDeliveryDate: normalizeTemplateValue(order.desiredDeliveryDate) ?? null,
      coupon: normalizeTemplateValue(order.coupon) ?? null,
      paymentMethodCode: null,
      paymentMethodDescription: normalizeTemplateValue(order.paymentMethodDescription) ?? null,
      paymentConditionCode: null,
      paymentConditionDescription: normalizeTemplateValue(order.paymentConditionDescription) ?? null,
      b2bUnitUid: String(order.unitUid ?? ''),
      customerUid: String(order.customerUid ?? ''),
      entries,
      consignments,
      // Orders from Generic API sync are always "REMOTE" — they came from the server
      localSyncStatus: 'REMOTE' as const,
      isLocalOrder: false,
    };

    if (consignments.length === 0) {
      return [
        {
          ...base,
          id: `${orderCode}_none`,
          consignmentCode: null,
          erpOrderCode: null,
          sapStatus: null,
        },
      ];
    }

    return consignments.map(c => ({
      ...base,
      id: `${orderCode}_${c.code ?? 'none'}`,
      consignmentCode: c.code || null,
      erpOrderCode: c.erpOrderCode,
      sapStatus: c.sapStatus,
    }));
  });
  console.log(teste);
  return teste;
}
